import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Input } from 'src/components/ui/input';
import {
  PlusIcon,
  Trash2,
  ChevronDown,
  Sparkles,
  Check,
  X,
  ListChecks,
} from 'lucide-react';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import { MonthYearRangePicker } from 'src/components/common/MonthYearRangePickerDatePicker';
import {
  useAddWorkExperienceDuty,
  useUpdateWorkExperienceDuty,
  useDeleteWorkExperienceDuty,
  useEnhanceWorkExperienceResponsibility,
} from 'src/hooks/useResumeData';
import { useToast } from 'src/hooks/use-toast';
import { AddButton } from 'src/components/resume/common';

const isValidDateRange = (startMonth, startYear, endMonth, endYear) => {
  if (!startMonth || !startYear || !endMonth || !endYear) return true;

  const startDate = new Date(`${startYear}-${startMonth}-01`);
  const endDate = new Date(`${endYear}-${endMonth}-01`);

  return startDate <= endDate;
};

// Helpers for cross-entry period validation
const toMonthIndex = (year, month) => {
  if (!year || !month) return null;
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  if (Number.isNaN(y) || Number.isNaN(m)) return null;
  return y * 12 + (m - 1);
};

const getCurrentMonthIndex = () => {
  const now = new Date();
  return now.getFullYear() * 12 + now.getMonth(); // 0-based month
};

// Periods treated as half-open intervals: [start, endExclusive)
const buildPeriod = (employmentPeriod) => {
  const start = toMonthIndex(
    employmentPeriod?.startYear,
    employmentPeriod?.startMonth
  );
  if (start === null) return null;
  const isPresent = !!employmentPeriod?.present;
  const endExclusive = isPresent
    ? getCurrentMonthIndex() + 1
    : (() => {
        const end = toMonthIndex(
          employmentPeriod?.endYear,
          employmentPeriod?.endMonth
        );
        return end === null ? null : end + 1;
      })();
  if (endExclusive === null) return null;
  return { start, endExclusive, present: isPresent };
};

// Allow same-month boundary transitions: e.g. one job ending Feb 2021 and
// the next starting Feb 2021 is valid because we only track month/year, not
// exact dates. Two periods truly overlap only when they share more than the
// single boundary month, so we subtract 1 from each endExclusive before comparing.
const periodsOverlap = (a, b) =>
  a.start < b.endExclusive - 1 && b.start < a.endExclusive - 1;

const DUTY_DESC_MAX = 700;
const DUTY_MIN_CHARS = 20;

/** Set false to silence console diagnostics for duty delete / border masking */
const DEBUG_DUTY_BORDER_MASK =
  typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

function generateDutyTitleFromDescription(description) {
  const desc = String(description || '').trim();
  if (!desc) return '';

  const prefixFromSeparator = [':', ' – ', ' - ']
    .map((sep) => {
      if (!desc.includes(sep)) return '';
      const prefix = desc.split(sep)[0].trim();
      if (prefix.length >= 3 && prefix.length <= 50) return prefix;
      return '';
    })
    .find(Boolean);

  if (prefixFromSeparator) return prefixFromSeparator;

  return desc.split(/\s+/).slice(0, 4).join(' ');
}

const autoGrow = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

function WorkExperienceDutyRow({
  control,
  expIndex,
  dutyIndex,
  fieldId,
  onRemove,
  experienceId,
  setValue,
  getValues,
  liveDutyApi,
}) {
  const base = `workExperiences.${expIndex}.duties.${dutyIndex}`;
  const descVal = useWatch({ control, name: `${base}.description` });
  const dutyId = useWatch({ control, name: `${base}.id` });
  const titleVal = useWatch({ control, name: `${base}.title` });
  const descStr = String(descVal || '');
  const descTrim = descStr.trim();
  const { toast } = useToast();
  const [persisting, setPersisting] = useState(false);

  // Live duty CRUD + AI-enhance via TanStack mutations.
  const addDutyMutation = useAddWorkExperienceDuty();
  const updateDutyMutation = useUpdateWorkExperienceDuty();
  const deleteDutyMutation = useDeleteWorkExperienceDuty();
  const enhanceDutyMutation = useEnhanceWorkExperienceResponsibility();

  const descFieldRef = useRef(null);
  const titleFieldRef = useRef(null);
  const dutyItemRootRef = useRef(null);
  const savedDescRef = useRef(descTrim);
  const enhanceReqIdRef = useRef(0);
  const enhanceSourceRef = useRef(''); // keep suggestion visible for the input we enhanced

  const [status, setStatus] = useState('idle'); // idle | dirty | processing | ready | enhanced
  const [suggestion, setSuggestion] = useState('');
  const [enhancedTitle, setEnhancedTitle] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    savedDescRef.current = descTrim;
    setSuggestion('');
    setEnhancedTitle('');
    setStatus('idle');
    setConfirmingDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId]);

  useEffect(() => {
    if (status === 'processing') return;

    const saved = String(savedDescRef.current || '').trim();
    const enhancedSource = String(enhanceSourceRef.current || '').trim();

    if (!descTrim) {
      if (status !== 'idle') setStatus('idle');
      if (suggestion) setSuggestion('');
      return;
    }

    // Keep the v11 suggestion prompt visible until the user changes the input again.
    if (status === 'ready') {
      if (descTrim === enhancedSource) return;
      console.log('[WXP][duty] suggestion invalidated by edit', {
        expIndex,
        dutyIndex,
        prevEnhancedSourceLen: enhancedSource.length,
        currentLen: descTrim.length,
      });
      setSuggestion('');
      setEnhancedTitle('');
      setStatus('dirty');
      return;
    }

    if (descTrim !== saved) {
      if (status !== 'dirty') setStatus('dirty');
      if (suggestion) setSuggestion('');
      return;
    }

    // When user edits back to saved text, keep "enhanced" badge until they change again.
    if (status !== 'enhanced') setStatus('idle');
  }, [descTrim, status, suggestion]);

  useEffect(() => {
    if (!DEBUG_DUTY_BORDER_MASK || !dutyItemRootRef.current) return;
    const root = dutyItemRootRef.current;
    const wrap = root.querySelector('.duty-ta-wrap');
    const del = root.querySelector('.duty-del');
    if (!wrap || !del) return;
    const run = () => {
      const delCs = getComputedStyle(del);
      const wrapCs = getComputedStyle(wrap);
      const dataFocused = root.getAttribute('data-focused');
      const dataState = root.getAttribute('data-state');
      console.log('[duty-border-mask]', {
        expIndex,
        dutyIndex,
        dataFocused,
        dataState,
        delBoxShadow: delCs.boxShadow,
        delBackgroundColor: delCs.backgroundColor,
        wrapBorderTopColor: wrapCs.borderTopColor,
        wrapBackgroundColor: wrapCs.backgroundColor,
        hint:
          dataFocused === 'false' &&
          (dataState === 'dirty' || dataState === 'processing')
            ? 'Primary border often stays on dirty/processing while unfocused; mask colors must track data-state, not only focus.'
            : null,
      });
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [isFocused, status, expIndex, dutyIndex]);

  // While the two-step delete confirm is showing, it's the only action — hide
  // the Save / Enhance affordances so they don't compete with it.
  const showEnhance =
    isFocused &&
    status === 'dirty' &&
    descTrim.length >= DUTY_MIN_CHARS &&
    !confirmingDelete;

  // Let the user commit what they typed without running the AI enhance. Shown
  // alongside Enhance whenever the duty has unsaved content; clearing the dirty
  // state hides both affordances (the text is already in form state).
  const showSave =
    isFocused && status === 'dirty' && descTrim.length > 0 && !confirmingDelete;

  // Live-persist this duty — existing experiences only. A duty on a brand-new
  // (unsaved) experience has no parent id yet, so it's committed locally and
  // gets created embedded in the experience's create POST at Save Changes.
  const persistDuty = async (text, title) => {
    if (!liveDutyApi || !experienceId) return;
    setPersisting(true);
    try {
      if (dutyId != null) {
        await updateDutyMutation.mutateAsync({
          experienceId,
          dutyId,
          duty: { title, description: text },
        });
        toast({ message: 'Duty updated', variant: 'success' });
      } else {
        // The add response carries no id; the mutation also returns the WE's
        // duties so we can reconcile by matching the row we just saved.
        const serverDuties = await addDutyMutation.mutateAsync({
          experienceId,
          duty: { title, description: text },
        });
        const known = new Set(
          ((getValues && getValues(`workExperiences.${expIndex}.duties`)) || [])
            .map((d) => d?.id)
            .filter((id) => id != null)
        );
        const isNew = (d) => d?.id != null && !known.has(d.id);
        const matched =
          (serverDuties || []).find(
            (d) =>
              isNew(d) &&
              String(d.description || '').trim() === text &&
              String(d.title || '').trim() === title
          ) || (serverDuties || []).find(isNew);
        if (matched && setValue) {
          setValue(`${base}.id`, matched.id, { shouldDirty: false });
        }
        toast({ message: 'Duty added', variant: 'success' });
      }
      // Record the synced baseline so the Save Changes flush won't re-send it.
      if (setValue) {
        setValue(
          `${base}._baseline`,
          { title, description: text },
          { shouldDirty: false }
        );
      }
    } catch (e) {
      toast({
        message: 'Could not save duty. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPersisting(false);
    }
  };

  const handleSaveDuty = () => {
    const text = descTrim;
    const title =
      (enhancedTitle || '').trim() ||
      (titleVal || '').trim() ||
      generateDutyTitleFromDescription(text);
    savedDescRef.current = text;
    setSuggestion('');
    setEnhancedTitle('');
    setStatus('idle');
    titleFieldRef.current?.onChange(title);
    persistDuty(text, title);
  };

  // Two-step delete: live DELETE for an existing duty, else just drop locally.
  const handleConfirmDelete = async () => {
    if (liveDutyApi && experienceId && dutyId != null) {
      setPersisting(true);
      try {
        await deleteDutyMutation.mutateAsync({ experienceId, dutyId });
        toast({ message: 'Duty removed', variant: 'success' });
      } catch (e) {
        toast({
          message: 'Could not remove duty. Please try again.',
          variant: 'destructive',
        });
        setPersisting(false);
        return; // keep the row if the server delete failed
      }
      setPersisting(false);
    }
    onRemove();
  };

  async function handleEnhance() {
    console.log('[WXP][duty] enhance clicked', {
      expIndex,
      dutyIndex,
      status,
      showEnhance,
      minChars: DUTY_MIN_CHARS,
      len: descTrim.length,
    });
    if (status !== 'dirty' || descTrim.length < DUTY_MIN_CHARS) return;

    enhanceReqIdRef.current += 1;
    const reqId = enhanceReqIdRef.current;
    const currentDesc = descTrim;

    enhanceSourceRef.current = currentDesc;
    setSuggestion('');
    setEnhancedTitle('');
    setStatus('processing');

    try {
      const res = await enhanceDutyMutation.mutateAsync(currentDesc);
      // The enhance call might return:
      // - axios response (res.data = full payload)
      // - already-unwrapped full payload
      // - (incorrectly) res.data = { title, description } (inner data)
      const root = res?.data ?? res ?? {};
      const payload =
        root && typeof root === 'object' && 'success' in root
          ? root
          : root || {};
      const dataBlock =
        payload && typeof payload === 'object' && payload.data
          ? payload.data
          : payload;

      const apiTitle = (dataBlock?.title || '').toString().trim();
      const apiDescription = (dataBlock?.description || '').toString().trim();
      const ok =
        payload?.success === true ||
        payload?.success === 'true' ||
        payload?.status_code === 200 ||
        !!apiDescription;

      console.log('[WXP][duty] enhance response', {
        expIndex,
        dutyIndex,
        reqId,
        ok,
        success: payload?.success,
        status_code: payload?.status_code,
        payloadKeys:
          payload && typeof payload === 'object' ? Object.keys(payload) : [],
        dataKeys:
          dataBlock && typeof dataBlock === 'object'
            ? Object.keys(dataBlock)
            : [],
        apiTitleLen: apiTitle.length,
        apiDescriptionLen: apiDescription.length,
      });

      if (enhanceReqIdRef.current !== reqId) return;

      if (ok && apiDescription) {
        const stableTitle =
          apiTitle || generateDutyTitleFromDescription(currentDesc);
        setEnhancedTitle(stableTitle);
        titleFieldRef.current?.onChange(stableTitle);
        setSuggestion(apiDescription);
        setStatus('ready');
        console.log('[WXP][duty] suggestion shown', {
          expIndex,
          dutyIndex,
          titleLen: stableTitle.length,
          suggestionLen: apiDescription.length,
        });
      } else {
        console.log('[WXP][duty] enhance did not yield suggestion', {
          expIndex,
          dutyIndex,
          ok,
          apiDescriptionLen: apiDescription.length,
        });
        setStatus('dirty');
      }
    } catch (e) {
      if (enhanceReqIdRef.current !== reqId) return;
      console.log('[WXP][duty] enhance failed', { expIndex, dutyIndex, e });
      setStatus('dirty');
      setSuggestion('');
      setEnhancedTitle('');
    }
  }

  const handleAccept = () => {
    if (!suggestion) return;

    const accepted = suggestion.trim();
    const stableTitle =
      (enhancedTitle || '').trim() ||
      generateDutyTitleFromDescription(descTrim);

    savedDescRef.current = accepted;
    setSuggestion('');
    setEnhancedTitle(stableTitle);
    setStatus('enhanced');

    descFieldRef.current?.onChange(accepted);
    titleFieldRef.current?.onChange(stableTitle);

    // Accepting the AI suggestion commits the duty, so persist it live (same as
    // the inline Save). Without this the enhanced text only reaches the server
    // via the Save-Changes flush.
    persistDuty(accepted, stableTitle);
  };

  const handleDismiss = () => {
    // Keep original description, but keep the (API) title the same.
    const stableTitle =
      (enhancedTitle || '').trim() ||
      generateDutyTitleFromDescription(descTrim);

    savedDescRef.current = descTrim;
    setSuggestion('');
    setEnhancedTitle(stableTitle);
    setStatus('enhanced');
    titleFieldRef.current?.onChange(stableTitle);
  };

  let charCls = '';
  if (descStr.length >= DUTY_DESC_MAX - 20) charCls = 'at-limit';
  else if (descStr.length >= DUTY_DESC_MAX - 50) charCls = 'near-limit';

  return (
    <div
      ref={dutyItemRootRef}
      className="duty-item"
      data-state={status}
      data-focused={isFocused}
    >
      <Controller
        name={`${base}.title`}
        control={control}
        render={({ field }) => {
          titleFieldRef.current = field;
          return (
            <input
              type="hidden"
              ref={field.ref}
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          );
        }}
      />
      <div className="duty-ta-wrap">
        <span className="duty-ta-num">{dutyIndex + 1}.</span>
        <Controller
          name={`${base}.description`}
          control={control}
          render={({ field }) => {
            descFieldRef.current = field;
            return (
              <textarea
                maxLength={DUTY_DESC_MAX}
                placeholder="Describe what you did…"
                className="duty-ta"
                style={{ resize: 'none', overflow: 'hidden' }}
                ref={(el) => {
                  field.ref(el);
                  autoGrow(el);
                }}
                value={(field.value ?? '').toString().slice(0, DUTY_DESC_MAX)}
                onFocus={() => setIsFocused(true)}
                onBlur={(e) => {
                  setIsFocused(false);
                  field.onBlur(e);
                }}
                onInput={(e) => {
                  const v = e.target.value.slice(0, DUTY_DESC_MAX);
                  field.onChange(v);
                  titleFieldRef.current?.onChange(
                    generateDutyTitleFromDescription(v)
                  );
                  autoGrow(e.target);
                }}
              />
            );
          }}
        />
        <button
          type="button"
          className="duty-del"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setConfirmingDelete(true)}
          title="Remove duty"
          aria-label="Remove duty"
        >
          <Trash2 />
        </button>
      </div>
      {confirmingDelete ? (
        <div className="duty-del-confirm">
          <span className="duty-del-confirm-text">Delete this duty?</span>
          <div className="duty-del-confirm-actions">
            <button
              type="button"
              className="duty-del-cancel"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="duty-del-go"
              onClick={handleConfirmDelete}
              disabled={persisting}
            >
              Delete
            </button>
          </div>
        </div>
      ) : null}
      <div className="duty-footer">
        {isFocused ? (
          <span className={`duty-chars ${charCls}`}>
            {Math.min(descStr.length, DUTY_DESC_MAX)}/{DUTY_DESC_MAX}
          </span>
        ) : (
          <span />
        )}
        <span className="duty-footer-right">
          <span className="duty-proc">
            <span className="proc-dots">
              <span className="proc-dot" />
              <span className="proc-dot" />
              <span className="proc-dot" />
            </span>
            Enhancing…
          </span>
          <span className="duty-enhanced-badge">
            <Sparkles className="ai-sug-sparkle" /> Enhanced
          </span>
          {showSave ? (
            <button
              type="button"
              className="duty-commit-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSaveDuty}
              disabled={persisting}
            >
              <Check />
              Save
            </button>
          ) : null}
          {showEnhance ? (
            <button
              type="button"
              className="duty-save-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleEnhance}
            >
              <Sparkles />
              Enhance
            </button>
          ) : null}
        </span>
      </div>
      <div className="ai-sug">
        <div className="ai-sug-head">
          <Sparkles className="ai-sug-sparkle" />
          <span className="ai-sug-label">AI-enhanced version</span>
        </div>
        <div className="ai-sug-text">{suggestion}</div>
        <div className="ai-sug-actions">
          <button
            type="button"
            className="ai-btn ai-btn-accept"
            onClick={handleAccept}
          >
            <Check /> Accept
          </button>
          <button
            type="button"
            className="ai-btn ai-btn-dismiss"
            onClick={handleDismiss}
          >
            <X /> Keep original
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkExperienceDutiesSection({
  control,
  index,
  experienceId,
  setValue,
  getValues,
  liveDutyApi,
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `workExperiences.${index}.duties`,
  });
  const [stackExpanded, setStackExpanded] = useState(false);
  const useStack = fields.length > 2;

  const dutyRows = fields.map((field, dutyIndex) => (
    <WorkExperienceDutyRow
      key={field.id}
      fieldId={field.id}
      control={control}
      expIndex={index}
      dutyIndex={dutyIndex}
      onRemove={() => remove(dutyIndex)}
      experienceId={experienceId}
      setValue={setValue}
      getValues={getValues}
      liveDutyApi={liveDutyApi}
    />
  ));

  return (
    <div className="wxp-duty-edit pt-4 mt-1 border-t border-[var(--border-light)]">
      {!useStack && (
        <div className="duties-label">
          <span className="duties-label-text">Duties & Responsibilities</span>
          <span className="duties-label-count">{fields.length}</span>
          <span className="duties-label-line" />
        </div>
      )}

      {useStack ? (
        <div
          className={`completed-stack duty-stack${stackExpanded ? ' expanded' : ''}`}
        >
          <div
            className="stack-trigger"
            onClick={() => setStackExpanded(!stackExpanded)}
          >
            <div className="stack-header duty-stack-header">
              <div className="stack-icon">
                <ListChecks />
              </div>
              <div className="stack-info">
                <div className="stack-title">Duties & Responsibilities</div>
                <div className="stack-sub">{fields.length} items</div>
              </div>
              <div className="stack-chevron">
                <ChevronDown style={{ width: '16px', height: '16px' }} />
              </div>
            </div>
          </div>
          <div className="stack-cards duty-stack-cards">{dutyRows}</div>
        </div>
      ) : (
        dutyRows
      )}

      <button
        type="button"
        onClick={() => {
          append({ title: '', description: '' });
          // fields.length >= 2 means after append the count will be > 2 → stack activates
          if (fields.length >= 2) setStackExpanded(true);
          setTimeout(() => {
            const tas = document.querySelectorAll('.wxp-duty-edit .duty-ta');
            if (!tas.length) return;
            const lastTa = tas[tas.length - 1];
            autoGrow(lastTa);
            lastTa.focus();
            lastTa.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 150);
        }}
        className="duty-add-btn"
      >
        <PlusIcon />
        Add duty
      </button>
    </div>
  );
}

export const WorkExperienceFields = ({
  index,
  control,
  errors,
  onRemove,
  watch,
  setValue,
  getValues,
  validationActive = false,
  missingLabels = [],
  validationKey = 0,
  // When true, duty edits hit the live duty CRUD endpoints (private dashboard).
  // Public-link / submit flows leave this off and persist duties in bulk.
  liveDutyApi = false,
  // Controlled accordion props – if provided, parent manages open/close
  isExpanded: isExpandedProp,
  onToggle,
}) => {
  const [isExpandedLocal, setIsExpandedLocal] = useState(false);
  const [confirmExpDelete, setConfirmExpDelete] = useState(false);
  // Use controlled value when parent supplies it, otherwise fall back to local state
  const isExpanded =
    isExpandedProp !== undefined ? isExpandedProp : isExpandedLocal;
  const setIsExpanded = (val) => {
    if (onToggle) {
      onToggle(val);
    } else {
      setIsExpandedLocal(val);
    }
  };
  const roleInputRef = useRef(null);
  const companyInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const autoOpenedNewRef = useRef(false);
  const lastAutoOpenValidationKeyRef = useRef(0);

  // Get current values for display
  const allExperiences =
    watch && typeof watch === 'function' ? watch('workExperiences') || [] : [];
  const currentExp = allExperiences[index] || {};
  const role = typeof currentExp.role === 'string' ? currentExp.role : '';
  const company =
    typeof currentExp.company === 'string' ? currentExp.company : '';
  const period = currentExp.employment_period || {};
  const isPresent = !!period.present;

  // Check if we can delete (multiple entries or onRemove is provided)
  const canDelete = onRemove || allExperiences.length > 1;

  // Determine when to hide the "Present" checkbox
  const normalizedFromWatch = allExperiences
    .map((exp, i) => ({
      index: i,
      period: buildPeriod(exp?.employment_period),
    }))
    .filter((p) => p.period !== null);
  const latestFromWatch =
    normalizedFromWatch.length > 0
      ? normalizedFromWatch.reduce((acc, p) =>
          p.period.start > acc.period.start ? p : acc
        )
      : null;
  const isLatestRow = !!latestFromWatch && latestFromWatch.index === index;
  const hasPresent = normalizedFromWatch.some((p) => p.period.present);
  const presentOnLatest =
    !!latestFromWatch &&
    normalizedFromWatch.some(
      (p) => p.index === latestFromWatch.index && p.period.present
    );
  const thisRowHasEnd = !!(period.endMonth && period.endYear);
  const hidePresent =
    (hasPresent && presentOnLatest && !isLatestRow) || thisRowHasEnd;

  // Check if this entry is new/empty (no data filled)
  const isNewEntry =
    !role &&
    !company &&
    !period.startMonth &&
    !period.startYear &&
    !period.endMonth &&
    !period.endYear &&
    !period.present;

  const hasAnyValue =
    !!role ||
    !!company ||
    !!period.startMonth ||
    !!period.startYear ||
    !!period.endMonth ||
    !!period.endYear ||
    !!period.present;

  // Check if this entry has any validation errors
  const hasErrors =
    errors?.workExperiences?.[index]?.role ||
    errors?.workExperiences?.[index]?.company ||
    errors?.workExperiences?.[index]?.employment_period;

  const rowHasMissing = validationActive && (missingLabels?.length || 0) > 0;
  const rowComplete =
    validationActive && hasAnyValue && (missingLabels?.length || 0) === 0;
  const missingSet = useMemo(
    () => new Set((missingLabels || []).map((s) => String(s || '').trim())),
    [missingLabels]
  );

  const roleMissing = hasAnyValue && String(role).trim().length === 0;
  const companyMissing = hasAnyValue && String(company).trim().length === 0;
  const fromMissing = hasAnyValue && !(period.startMonth && period.startYear);
  const toMissing =
    hasAnyValue && !period.present && !(period.endMonth && period.endYear);

  const showRoleError =
    !!errors?.workExperiences?.[index]?.role ||
    (validationActive && roleMissing);
  const showCompanyError =
    !!errors?.workExperiences?.[index]?.company ||
    (validationActive && companyMissing);
  const showPeriodError =
    !!errors?.workExperiences?.[index]?.employment_period ||
    (validationActive && (fromMissing || toMissing));

  const hasMissingForUX =
    validationActive &&
    (roleMissing || companyMissing || fromMissing || toMissing);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!validationActive) return;
    // Debug: verify missingLabels reach the row and how we decide borders.
    // eslint-disable-next-line no-console
    console.warn('[experience-validation] fields', {
      index,
      validationActive,
      missingLabels,
      missingSet: Array.from(missingSet),
      errorsRow: errors?.workExperiences?.[index],
    });
  }, [index, validationActive, missingLabels, missingSet, errors]);

  // Helper function to scroll element into view within its scrollable container
  const scrollIntoViewInContainer = (element) => {
    if (!element) return;

    // Find the nearest scrollable parent (edit-form-body)
    let scrollContainer = element.closest('.edit-form-body');
    if (!scrollContainer) {
      // Fallback: find any scrollable parent
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          scrollContainer = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate scroll position to center the element
      const scrollTop =
        scrollContainer.scrollTop +
        (elementRect.top - containerRect.top) -
        containerRect.height / 2 +
        elementRect.height / 2;

      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    } else {
      // Fallback to default scrollIntoView
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Auto-expand new entries and focus on first input field
  useEffect(() => {
    // Only auto-open a brand-new empty entry once (otherwise user can never close it).
    if (isNewEntry && !isExpanded && !autoOpenedNewRef.current) {
      autoOpenedNewRef.current = true;
      setIsExpanded(true);
      // Focus on the role input field after a short delay to allow accordion to expand
      setTimeout(() => {
        if (roleInputRef.current) {
          roleInputRef.current.focus();
          scrollIntoViewInContainer(roleInputRef.current);
        }
      }, 300); // Wait for accordion animation
    }
  }, [isNewEntry, isExpanded]);

  // Auto-expand accordion and focus on first error field
  useEffect(() => {
    // Only auto-open on validation attempts (keyed by Save), so it doesn't "fight" user toggles.
    if (!validationActive) return;
    if (!validationKey) return;
    // Mark this validation attempt as handled even if the accordion is already open.
    // Otherwise the first user collapse can be immediately counteracted by this effect,
    // making it feel like collapse works only on the second click.
    if (validationKey === lastAutoOpenValidationKeyRef.current) return;
    lastAutoOpenValidationKeyRef.current = validationKey;

    if (!hasMissingForUX) return;

    if (!isExpanded) {
      setIsExpanded(true);
    }

    // Focus/scroll the first missing field (even if already expanded).
    setTimeout(() => {
      if (roleMissing && roleInputRef.current) {
        roleInputRef.current.focus();
        scrollIntoViewInContainer(roleInputRef.current);
        return;
      }
      if (companyMissing && companyInputRef.current) {
        companyInputRef.current.focus();
        scrollIntoViewInContainer(companyInputRef.current);
        return;
      }
      if ((fromMissing || toMissing) && datePickerRef.current) {
        scrollIntoViewInContainer(datePickerRef.current);
      }
    }, 350);
  }, [
    validationActive,
    validationKey,
    hasMissingForUX,
    isExpanded,
    roleMissing,
    companyMissing,
    fromMissing,
    toMissing,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    // eslint-disable-next-line no-console
    console.warn('[experience-accordion] row-state', {
      index,
      isExpanded,
      isNewEntry,
      validationActive,
      validationKey,
      hasErrors: !!hasErrors,
    });
  }, [
    index,
    isExpanded,
    isNewEntry,
    validationActive,
    validationKey,
    hasErrors,
  ]);

  return (
    <div
      className={`entry-card edu-entry-card group border border-ah-surface-card-border rounded-lg overflow-hidden bg-[var(--surface)] transition-all duration-300 ease-in-out ${
        isExpanded
          ? 'active edu-entry-card--active border-blue-400 shadow-md'
          : 'shadow-sm'
      } ${rowHasMissing ? 'edu-entry-card--error' : ''} ${
        rowComplete ? 'edu-entry-card--complete' : ''
      }`}
    >
      {/* Entry Header */}
      <div
        role="button"
        tabIndex={0}
        className="entry-head flex items-center px-3.5 py-3 gap-2.5 cursor-pointer select-none transition-colors duration-200 hover:bg-[var(--surface-muted)]"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="entry-num w-[26px] h-[26px] rounded-full bg-ah-surface-card-border text-ah-brand-primary-form text-xs font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </div>
        <div className="entry-summary flex-1 min-w-0">
          <div className="entry-summary-title text-sm font-semibold text-[var(--text)] truncate">
            {role || 'New Entry'}
          </div>
          <div className="entry-summary-sub text-xs text-[var(--text-faint)] truncate">
            {company ? (
              <>
                {company}
                {isPresent && (
                  <span className="text-[var(--success)]"> · Current</span>
                )}
              </>
            ) : (
              'Tap to fill details'
            )}
          </div>
        </div>
        <div className="entry-actions flex items-center gap-1.5 flex-shrink-0">
          {/* Delete moved to an always-visible "Delete this experience" button at
              the bottom of the expanded form — the old header trash was
              hover-only (opacity-0 group-hover), so it was unreachable on touch. */}
          <div
            className={`entry-toggle flex items-center justify-center text-[var(--text-faint)] transition-transform duration-300 ease-in-out ${
              isExpanded ? 'rotate-180' : 'rotate-0'
            }`}
          >
            <ChevronDown size={16} />
          </div>
        </div>
      </div>

      {rowHasMissing && (missingLabels?.length || 0) > 0 && (
        <div className="edu-entry-error-detail">
          {missingLabels.map((label) => (
            <span key={`${index}-${label}`} className="edu-error-field-tag">
              <svg viewBox="0 0 8 8" aria-hidden>
                <circle cx="4" cy="4" r="3" fill="currentColor" opacity="0.5" />
              </svg>{' '}
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Entry Form */}
      <div
        className={`entry-form overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          isExpanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          className={`entry-form-inner px-3.5 pb-3.5 border-t border-[var(--border-light)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isExpanded
              ? 'translate-y-0 opacity-100'
              : '-translate-y-4 opacity-0'
          }`}
          style={{
            transitionDelay: isExpanded ? '50ms' : '0ms',
          }}
        >
          <div
            className={`space-y-4 mt-3 transition-opacity duration-300 ${
              isExpanded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              transitionDelay: isExpanded ? '100ms' : '0ms',
            }}
          >
            {/* Job Title */}
            <div className="fg">
              <Label className="fl block text-xs font-semibold text-gray-500 mb-1.5">
                Job Title
              </Label>
              <Controller
                name={`workExperiences.${index}.role`}
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    const experience = formValues.workExperiences[index];
                    const empPeriod = experience?.employment_period || {};

                    // Check if entry has any data
                    const hasAnyValue =
                      experience.company ||
                      experience.role ||
                      empPeriod.startMonth ||
                      empPeriod.startYear ||
                      empPeriod.endMonth ||
                      empPeriod.endYear ||
                      empPeriod.present;

                    // If entry has any data, role is required
                    if (hasAnyValue && !value) {
                      return 'Job Title is required';
                    }

                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    ref={roleInputRef}
                    type="text"
                    placeholder="Enter job title"
                    className={`fi w-full px-3 py-2.5 rounded-md border border-ah-surface-card-border bg-white text-sm text-gray-900 transition-colors outline-none focus:border-ah-brand-primary-form focus:shadow-[0_0_0_3px_rgba(68,114,196,0.2)] ${
                      showRoleError ? 'edu-fi edu-fi--error' : ''
                    }`}
                  />
                )}
              />
            </div>

            {/* Company */}
            <div className="fg">
              <Label className="fl block text-xs font-semibold text-gray-500 mb-1.5">
                Company
              </Label>
              <Controller
                name={`workExperiences.${index}.company`}
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    const experience = formValues.workExperiences[index];
                    const empPeriod = experience?.employment_period || {};

                    // Check if entry has any data
                    const hasAnyValue =
                      experience.company ||
                      experience.role ||
                      empPeriod.startMonth ||
                      empPeriod.startYear ||
                      empPeriod.endMonth ||
                      empPeriod.endYear ||
                      empPeriod.present;

                    // If entry has any data, company is required
                    if (hasAnyValue && !value) {
                      return 'Company is required';
                    }

                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    ref={companyInputRef}
                    type="text"
                    placeholder="Enter company name"
                    className={`fi w-full px-3 py-2.5 rounded-md border border-ah-surface-card-border bg-white text-sm text-gray-900 transition-colors outline-none focus:border-ah-brand-primary-form focus:shadow-[0_0_0_3px_rgba(68,114,196,0.2)] ${
                      showCompanyError ? 'edu-fi edu-fi--error' : ''
                    }`}
                  />
                )}
              />
            </div>

            {/* Currently working here checkbox */}
            {!hidePresent && (
              <div className="fg">
                <Label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--text)] select-none py-1">
                  <Controller
                    name={`workExperiences.${index}.employment_period`}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      const currentPeriod = value || {};
                      const isChecked = !!currentPeriod.present;
                      return (
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            onChange({
                              ...currentPeriod,
                              present: !!checked,
                              ...(checked ? { endMonth: '', endYear: '' } : {}),
                            });
                          }}
                          variant="success"
                          className="h-4 w-4"
                        />
                      );
                    }}
                  />
                  <span className="text-xs font-semibold text-[var(--text-faint)]">
                    Currently working here
                  </span>
                  {isPresent && (
                    <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[var(--success-light)] text-[var(--success)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] animate-pulse" />
                      Present
                    </span>
                  )}
                </Label>
              </div>
            )}

            {/* Date Range */}
            <div className="fg" ref={datePickerRef}>
              <Controller
                name={`workExperiences.${index}.employment_period`}
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    const experience = formValues.workExperiences[index];
                    const periodValue = value || {};

                    // Check if entry has any data
                    const hasAnyValue =
                      experience.company ||
                      experience.role ||
                      periodValue.startMonth ||
                      periodValue.startYear ||
                      periodValue.endMonth ||
                      periodValue.endYear ||
                      periodValue.present;

                    // If entry has no data, skip validation
                    if (!hasAnyValue) return true;

                    // If entry has data, validate required fields
                    const hasAllStart =
                      periodValue.startMonth && periodValue.startYear;
                    const rowIsPresent = !!periodValue.present;
                    const hasAllEnd =
                      periodValue.endMonth && periodValue.endYear;

                    if (!hasAllStart) {
                      return 'From date is required';
                    }

                    if (!rowIsPresent && !hasAllEnd) {
                      return 'Please complete the To date or mark "Currently working here"';
                    }

                    // Validate start/end order for non-present entries (without early return)
                    if (!rowIsPresent) {
                      const dateRangeValid = isValidDateRange(
                        value.startMonth,
                        value.startYear,
                        value.endMonth,
                        value.endYear
                      );
                      if (!dateRangeValid) {
                        return 'End date must be after start date';
                      }
                    }

                    // Cross-entry validations (runs for ALL entries, not just present)
                    const experiences = Array.isArray(
                      formValues.workExperiences
                    )
                      ? formValues.workExperiences
                      : [];

                    const normalized = experiences
                      .map((exp, i) => ({
                        index: i,
                        period: buildPeriod(exp?.employment_period),
                      }))
                      .filter((p) => p.period !== null);

                    const presentEntries = normalized.filter(
                      (p) => p.period.present
                    );
                    if (presentEntries.length > 1 && rowIsPresent) {
                      return 'Only one current job is allowed';
                    }

                    const current = normalized.find((p) => p.index === index);
                    if (current) {
                      const clash = normalized.find(
                        (p) =>
                          p.index !== index &&
                          periodsOverlap(current.period, p.period)
                      );
                      if (clash) {
                        return `This period overlaps with entry #${
                          clash.index + 1
                        }`;
                      }
                    }

                    if (rowIsPresent && current) {
                      const maxStart = Math.max(
                        ...normalized.map((p) => p.period.start)
                      );
                      if (current.period.start !== maxStart) {
                        return 'Current job must be the latest entry';
                      }
                    }

                    return true;
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <MonthYearRangePicker
                    value={value || {}}
                    onChange={onChange}
                    hidePresent
                    hasError={showPeriodError}
                  />
                )}
              />
              {errors?.workExperiences?.[index]?.employment_period && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.workExperiences[index].employment_period.message}
                </p>
              )}
            </div>

            <WorkExperienceDutiesSection
              control={control}
              index={index}
              experienceId={currentExp.id ?? null}
              setValue={setValue}
              getValues={getValues}
              liveDutyApi={liveDutyApi}
            />

            {/* Always-visible delete control for the whole experience entry.
                Two-step: a destructive trigger reveals an inline confirm. */}
            {canDelete && (
              <div className="exp-del-zone">
                {confirmExpDelete ? (
                  <div className="exp-del-confirm">
                    <span className="exp-del-confirm-text">
                      Delete this experience?
                    </span>
                    <div className="exp-del-confirm-actions">
                      <button
                        type="button"
                        className="exp-del-cancel"
                        onClick={() => setConfirmExpDelete(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="exp-del-go"
                        onClick={() => {
                          if (onRemove) onRemove();
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="exp-del-trigger"
                    onClick={() => setConfirmExpDelete(true)}
                  >
                    <Trash2 />
                    <span>Delete this experience</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AddExperienceButton = ({ onClick }) => (
  <AddButton onClick={onClick} text="Add Work Experience" />
);
